import React from 'react';
import { Modal, Alert, message } from 'antd';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import { selectedInfoSelector } from '../../../../../../store/selectors';
import { formatDate } from '../../../../../../utils';
import useAddBinds from '../../../../../../mutations/useAddBinds';
import useDeleteInfoBind from '../../../../../../mutations/useDeleteInfoBind';
import styles from './ConfirmModal.module.scss';

const getAddressTitle = (item) => {
  const typeShortName = item.typeShortName?.trim();
  const addressName = item.flat || item.name || item.addressName;

  return [typeShortName, addressName].filter(Boolean).join(' ');
};

const createListDataSource = (data) => {
  const source = [];

  if (data?.companies.length > 0) {
    source.push({
      title: 'Все помещения УК:',
      items: data.companies,
      renderItem: (item) => item.name,
    });
  }

  if (data?.houses.length > 0) {
    source.push({
      title: 'Все помещения дома:',
      items: data.houses,
      renderItem: (item) => (
        <>
          <b>{item.fullName || item.house || item.id}</b> ({item.streetName})
        </>
      ),
    });
  }

  if (data?.addresses.length > 0) {
    source.push({
      title: 'Помещения:',
      items: data.addresses,
      renderItem: (item) => {
        const meta = [
          item.streetName && `${item.streetName}`,
          item.building && `, д. ${item.building}`,
          item.corpus && `, к. ${item.corpus}`,
        ]
          .filter(Boolean)
          .join('');

        return (
          <>
            <b>{getAddressTitle(item)}</b> ({meta})
          </>
        );
      },
    });
  }

  return source.filter(Boolean);
};

const Text = ({ accent = false, className, ...props }) => (
  <p
    {...props}
    className={cn(styles.text, className, {
      [styles.accent]: accent,
    })}
  />
);

const ListItem = ({ title, items, renderItem }) => {
  return (
    <li>
      <div className={styles.cardHead}>
        <Text style={{ color: '#009fdc' }}>
          <b>{title}</b>
        </Text>
      </div>
      <div className={styles.cardBody}>
        {items.map((item, index) => (
          <Text
            key={index}
            className={styles.cardBodyItem}
          >
            {renderItem(item)}
          </Text>
        ))}
      </div>
    </li>
  );
};

const Column = (props) => (
  <div
    {...props}
    className={styles.column}
  />
);

const ConfirmModal = ({ data, onCancel, afterSubmit }) => {
  const info = useSelector(selectedInfoSelector);

  const { addBinds, loading } = useAddBinds();
  const { deleteBindsAsync, loading: deleteLoading } = useDeleteInfoBind();

  const duplicateHouseNames =
    data?.duplicateHouses?.map(
      (house) =>
        `${house.fullName || house.house || house.id}${
          house.streetName ? ` (${house.streetName})` : ''
        }`
    ) || [];

  const handleSubmit = async () => {
    if (duplicateHouseNames.length > 0) {
      message.warning(
        `Дом уже привязан: ${duplicateHouseNames.join(
          ', '
        )}. Запрос не был отправлен.`
      );
      return;
    }

    if (data.removedBindIds?.length > 0) {
      await deleteBindsAsync(data.removedBindIds);
    }

    if (Object.keys(data.formValues).length > 0) {
      await addBinds({ ...data.formValues, infoId: info.id });
    }

    onCancel();
    afterSubmit();
  };

  const listDataSource = createListDataSource(data?.modalData);

  return (
    <Modal
      width={520}
      title="Информирование"
      visible={Boolean(data)}
      onCancel={onCancel}
      okText="Запустить"
      onOk={handleSubmit}
      confirmLoading={loading || deleteLoading}
      bodyStyle={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
    >
      <Column>
        <Text accent>Заголовок</Text>
        <Text>{info?.title || '-'}</Text>
      </Column>
      <Column>
        <Text accent>Период</Text>
        <Text>{`${formatDate(info.datePub)} - ${formatDate(
          info.dateEnd
        )}`}</Text>
      </Column>
      <Column>
        <Text accent>Получатели</Text>
        <ul className={styles.list}>
          {listDataSource.map((source, index) => (
            <ListItem
              key={index}
              title={source.title}
              items={source.items}
              renderItem={source.renderItem}
            />
          ))}
        </ul>
      </Column>
      {duplicateHouseNames.length > 0 && (
        <Alert
          message={`Дом уже привязан: ${duplicateHouseNames.join(
            ', '
          )}. Отправка будет заблокирована.`}
          type="warning"
          showIcon
        />
      )}
      <Alert
        message="Рассылка сообщения начнется автоматически, в соответствии с назначенным периодом."
        type="warning"
        showIcon
      />
    </Modal>
  );
};

export default ConfirmModal;
